import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, ArrowClockwise, CircleNotch, Play, ArrowSquareOut, EyeSlash } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { AdminThumb } from "@/components/admin/AdminThumb";

const REASON_STYLE = {
  dead_link: "border-crimson/60 text-crimson",
  dead_trailer: "border-crimson/60 text-crimson",
  title_mismatch: "border-amber/60 text-amber",
  unrecognized_tag: "border-violet-400/60 text-violet-300",
};
const btn = "flex items-center gap-1.5 border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-200 disabled:opacity-40";

const FlagRow = ({ f, headers, onDone }) => {
  const [busy, setBusy] = useState("");
  const resolve = async (restore) => {
    setBusy(restore ? "restore" : "dismiss");
    try {
      await axios.post(`${API}/admin/audit/weekly/${encodeURIComponent(f.video_id)}/resolve`, { restore }, { headers });
      toast.success(restore ? "Restored to the catalog" : "Marked as reviewed");
      onDone(f.video_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
      setBusy("");
    }
  };
  return (
    <li data-testid={`audit-flag-${f.video_id}`} className="flex flex-wrap items-center gap-4 border-b border-white/5 py-3">
      <AdminThumb url={f.url} thumb={f.thumb} testid={`audit-flag-thumb-${f.video_id}`} />
      <div className="min-w-[200px] flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm text-white/85" title={f.title}>{f.title}</p>
          {f.hidden && <span data-testid={`audit-hidden-${f.video_id}`} className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.15em] text-crimson"><EyeSlash size={11} /> Hidden</span>}
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {f.reasons.map((r) => (
            <span key={r.code} data-testid={`audit-reason-${f.video_id}-${r.code}`} title={r.detail}
              className={`border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] ${REASON_STYLE[r.code]}`}>{r.label}</span>
          ))}
        </div>
        <p className="mt-1 truncate font-mono text-[10px] text-white/35" title={f.reasons.map((r) => r.detail).join("\n")}>
          {f.reasons.map((r) => r.detail).join(" · ")}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Link to={`/watch/${f.video_id}`} target="_blank" data-testid={`audit-open-${f.video_id}`} className={`${btn} border-white/15 text-white/60 hover:border-amber hover:text-amber`}>
          <ArrowSquareOut size={12} /> Open & edit
        </Link>
        {f.hidden && (
          <button type="button" data-testid={`audit-restore-${f.video_id}`} disabled={!!busy} onClick={() => resolve(true)} className={`${btn} border-emerald-400/50 text-emerald-300 hover:bg-emerald-400/10`}>
            Restore
          </button>
        )}
        <button type="button" data-testid={`audit-dismiss-${f.video_id}`} disabled={!!busy} onClick={() => resolve(false)} className={`${btn} border-white/15 text-white/50 hover:border-white/40 hover:text-white`}>
          {busy === "dismiss" ? <CircleNotch size={12} className="animate-spin" /> : null} Dismiss
        </button>
      </div>
    </li>
  );
};

export const WeeklyAuditPanel = ({ headers }) => {
  const [data, setData] = useState(null);
  const [reason, setReason] = useState("");
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/audit/weekly`, { headers, params: reason ? { reason } : {} });
      setData(d);
      setRunning(d.last_run?.status === "running");
    } catch {
      setData({ items: [], labels: {} });
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reason]);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const runNow = async () => {
    try {
      await axios.post(`${API}/admin/audit/weekly/run`, {}, { headers });
      toast.success("Audit started — results appear here in a few minutes");
      setRunning(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the audit");
    }
  };

  const last = data?.last_run;
  const counts = last?.counts || {};
  return (
    <section data-testid="weekly-audit-panel" className="border border-white/10 bg-[#0A0A0A] p-6 sm:p-8">
      <div className="flex flex-wrap items-center gap-3">
        <ShieldCheck size={26} weight="duotone" className="text-amber" />
        <h2 className="font-serif text-2xl tracking-tight text-[#EDEDED]">Weekly Catalog Audit</h2>
        <button type="button" data-testid="audit-run-now" onClick={runNow} disabled={running}
          className={`${btn} ml-auto border-amber bg-amber text-black font-bold hover:bg-amber/80`}>
          {running ? <CircleNotch size={13} className="animate-spin" /> : <Play size={13} weight="fill" />} {running ? "Running…" : "Run audit now"}
        </button>
        <button type="button" data-testid="audit-refresh" onClick={load} className={`${btn} border-white/15 text-white/60 hover:border-amber hover:text-amber`}>
          <ArrowClockwise size={13} /> Refresh
        </button>
      </div>
      <p className="mt-3 font-mono text-[11px] leading-relaxed text-white/40">
        Runs every <span className="text-amber">Sunday 05:00 UTC</span>: pings every YouTube movie and trailer, compares our title with
        YouTube's, and checks genres against TMDb. Dead links are hidden automatically. Restore anything that was hidden by mistake.
      </p>
      <p data-testid="audit-last-run" className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-white/35">
        {last ? `Last run ${new Date(last.started_at).toLocaleString()} · ${last.status === "done" ? `${last.checked} checked · ${last.flagged} flagged · ${last.hidden} hidden` : "in progress"}` : "No audit has run yet"}
      </p>
      <div className="mt-5 flex flex-wrap gap-2" data-testid="audit-reason-filters">
        {[["", "All"], ...Object.entries(data?.labels || {})].map(([k, label]) => (
          <button key={k || "all"} type="button" data-testid={`audit-filter-${k || "all"}`} onClick={() => setReason(k)}
            className={`${btn} ${reason === k ? "border-amber bg-amber/10 text-amber" : "border-white/15 text-white/55 hover:text-white"}`}>
            {label}{k && counts[k] != null ? ` · ${counts[k]}` : ""}
          </button>
        ))}
      </div>
      {!data ? (
        <p className="mt-6 font-mono text-[11px] text-white/35">Loading…</p>
      ) : data.items.length === 0 ? (
        <p data-testid="audit-flags-empty" className="mt-6 font-mono text-[11px] text-white/35">Nothing flagged — the catalog looks clean.</p>
      ) : (
        <ul data-testid="audit-flags-list" className="mt-4 max-h-[720px] overflow-auto pr-1">
          {data.items.map((f) => (
            <FlagRow key={f.video_id} f={f} headers={headers} onDone={(id) => setData((d) => {
              const counts = { ...(d.last_run?.counts || {}) };
              f.reasons.forEach((r) => { counts[r.code] = Math.max(0, (counts[r.code] || 0) - 1); });
              return { ...d, last_run: d.last_run && { ...d.last_run, counts }, items: d.items.filter((x) => x.video_id !== id) };
            })} />
          ))}
        </ul>
      )}
    </section>
  );
};
